import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Tabs, Tab } from '@material-ui/core';
import useStyles from './styles';
import { convertObjToArr } from '../../../../utils';

const NavigationBar = props => {
  const { sections, hasMedia, scrollContainer, lesson } = props;

  const classes = useStyles();
  const refTabs = useRef(null);

  const [tabs, setTabs] = useState([]);
  const [showTabs, setShowTabs] = useState(false);
  const [activeTab, setActiveTab] = useState(false);
  const [sectionsProps, setSectionsProps] = useState([]);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [lastTabClicked, setLastTabClicked] = useState(null);
  const [windowSize, setWindowSize] = useState({
    width: window.outerWidth,
    height: window.outerHeight,
  });
  const [scrollProps, setScrollProps] = useState({
    containerHeight: 0,
    sectionsHeightAmmount: 0,
  });

  // Faz scroll até a section que foi clicada na tab
  const handleChangeClick = useCallback(
    (event, newValue) => {
      if (!sectionsProps.length) return;
      const index = sectionsProps.findIndex(section => section.id === newValue);
      if (sectionsProps[index]) {
        const offset =
          sectionsProps[index].offsetTop - refTabs.current.offsetHeight;
        setLastTabClicked(newValue);
        if (scrollContainer.current.scrollTo) {
          scrollContainer.current.scrollTo({
            behavior: 'smooth',
            top: offset,
          });
        } else {
          scrollContainer.current.scrollTop = offset;
        }
      }
    },
    [scrollContainer, sectionsProps],
  );

  // Verifica a section que esta ativa com base na posição do scroll e o offset das sections
  // É ativado pela mudança do scroll ou clique em uma tab
  const handleSectionActive = useCallback(
    (trigger = null) => {
      const { containerHeight } = scrollProps;
      if (!sectionsProps.length || !containerHeight) return;

      let activeSection = sectionsProps[0].id;
      const visibilityItems = {
        partialBottom: [],
        partialTop: [],
        total: [],
      };

      sectionsProps.forEach(item => {
        const topAbove = item.offsetTop < scrollPosition;
        const bottomAbove = item.offsetTop + item.offsetHeight < scrollPosition;
        const topBelow = item.offsetTop > scrollPosition + containerHeight;
        const bottomBelow =
          item.offsetTop + item.offsetHeight > scrollPosition + containerHeight;

        const totalVisible = !topAbove && !bottomBelow;
        const totalInvisible =
          (topAbove && bottomAbove) || (topBelow && bottomBelow);
        const partialVisibleTop = !topAbove && bottomBelow;
        const partialVisibleBottom = topAbove && !bottomAbove;

        if (totalInvisible) return;

        if (totalVisible) visibilityItems.total.push(item.id);
        if (partialVisibleTop) visibilityItems.partialTop.push(item.id);
        if (partialVisibleBottom) visibilityItems.partialBottom.push(item.id);
      });

      if (trigger === 'scroll' && lastTabClicked) {
        setLastTabClicked(null);
      }

      Object.keys(visibilityItems).forEach(key => {
        const visibilityLevel = visibilityItems[key];

        if (visibilityLevel.length) {
          if (lastTabClicked) {
            activeSection = visibilityLevel.find(
              item => item === lastTabClicked,
            );
          } else {
            [activeSection] = [visibilityLevel[0]];
          }
        }
      });

      if (activeSection) {
        setActiveTab(activeSection);
      }
    },
    [lastTabClicked, scrollPosition, scrollProps, sectionsProps],
  );

  // Monitora o scroll, com base no container, scroll props e sections
  const scrollListener = useCallback(() => {
    const lastPosition = scrollContainer.current.scrollTop;
    window.requestAnimationFrame(() => {
      if (lastTabClicked) {
        setLastTabClicked(null);
      }
      setScrollPosition(lastPosition);
    });
  }, [lastTabClicked, scrollContainer]);

  // Atualiza os tamanhos e offsets quando a tela é redimensionada
  const resizeListener = useCallback(() => {
    const lastSize = windowSize;
    const newSize = {
      width: window.outerWidth,
      height: window.outerHeight,
    };
    const sizeIsNotEqual =
      lastSize.width !== newSize.width || lastSize.height !== newSize.height;
    if (sizeIsNotEqual) {
      window.requestAnimationFrame(() => {
        setWindowSize(newSize);
      });
    }
  }, [windowSize]);

  // Manipula os tamanhos quando atualiza a janela, aula ou tabs
  const handleSizes = useCallback(() => {
    const sectionsNewProps = [];
    let ammountHeights = 0;

    tabs.forEach(tab => {
      const section = document.querySelector(`#${tab.value}`);
      if (section) {
        const { id, offsetTop, offsetHeight } = section;
        sectionsNewProps.push({ id, offsetTop, offsetHeight });
        ammountHeights += offsetHeight;
      }
    });
    setSectionsProps(sectionsNewProps);
    setScrollProps(oldProps => ({
      ...oldProps,
      scrollTop: scrollContainer.current.scrollTop,
      containerHeight: scrollContainer.current.offsetHeight,
      sectionsHeightAmmount: ammountHeights,
    }));
  }, [scrollContainer, tabs]);

  // Seta o array das tabs com base nas sections
  useEffect(() => {
    const sectionsArr = convertObjToArr(sections);
    const newTabs = sectionsArr
      .filter(s => s.render)
      .map(section => ({
        value: section.id,
        labelTab: section.title,
      }));
    setTabs(newTabs);
  }, [sections]);

  // Joga o scroll pro topo caso mude a aula ou o container
  useEffect(() => {
    if (scrollContainer.current.scrollTo) {
      scrollContainer.current.scrollTo({ top: 0 });
    } else {
      scrollContainer.current.scrollTop = 0;
    }
  }, [lesson, scrollContainer]);

  // Manipula os tamanhos quando atualiza a janela, aula ou tabs
  useEffect(() => handleSizes(), [windowSize, lesson, handleSizes]);

  // Monitora o scroll, com base no container, scroll props e sections
  useEffect(() => {
    const scrollContainerEl = scrollContainer.current;
    scrollContainerEl.addEventListener('scroll', scrollListener, {
      passive: true,
    });
    return () => {
      if (scrollContainerEl) {
        scrollContainerEl.removeEventListener('scroll', scrollListener);
      }
    };
  }, [scrollContainer, scrollListener, scrollProps, sectionsProps]);

  // Verifica a section que esta ativa com base na posição do scroll e o offset das sections
  // É ativado pelo clique
  useEffect(() => handleSectionActive(), [
    handleSectionActive,
    scrollProps,
    sectionsProps,
  ]);

  // Verifica a section que esta ativa com base na posição do scroll e o offset das sections
  // É ativado pela mudança do scroll
  useEffect(() => handleSectionActive('scroll'), [
    scrollPosition,
    handleSectionActive,
  ]);

  // Atualiza os tamanhos e offsets quando a tela é redimensionada
  useEffect(() => {
    window.addEventListener('resize', resizeListener, { passive: true });
    return () => {
      window.removeEventListener('resize', resizeListener);
    };
  }, [resizeListener]);

  // Verifica se é necessario mostrar as tabs, com base se todo o conteudo é visivel em tela
  useEffect(() => {
    setShowTabs(
      hasMedia ||
        scrollProps.containerHeight < scrollProps.sectionsHeightAmmount,
    );
  }, [hasMedia, scrollProps]);

  return (
    <Box className={classes.container} ref={refTabs}>
      {showTabs ? (
        <Tabs
          value={activeTab}
          onChange={handleChangeClick}
          variant="scrollable"
          scrollButtons="auto"
          className="tabs-container"
          indicatorColor="primary"
        >
          {tabs.map(tab => (
            <Tab
              key={tab.value}
              label={tab.labelTab}
              value={tab.value}
              classes={{
                root: classes.root,
                wrapper: classes.wrapper,
                selected: classes.selected,
              }}
            />
          ))}
        </Tabs>
      ) : null}
    </Box>
  );
};

export default NavigationBar;
